import { useCallback, useMemo } from 'react';
import { DraggableLocation } from 'react-beautiful-dnd';

import moveSpreadContentOperation from 'editor/src/store/design/operation/spreadManipulation/moveSpreadContentOperation';
import getDesignData from 'editor/src/store/design/selector/getDesignData';
import { getReorderableSpreadIndexes } from 'editor/src/store/design/util/spreadShape';
import { useDispatch, useSelector } from 'editor/src/store/hooks';

import { DisplayPage } from './displayPageUtils';

const NO_SPREADS: ReadonlySet<number> = new Set<number>();

// The spreads a drag in this list can speak for, in the order the list shows
// them. A spread split across several entries is left out: a drag of one entry
// cannot say which part of the spread should move.
function getEligibleSpreadIndexes(displayPages: DisplayPage[]): number[] {
  const entriesPerSpread = new Map<number, number>();
  displayPages.forEach(({ spreadIndex }) => {
    entriesPerSpread.set(spreadIndex, (entriesPerSpread.get(spreadIndex) ?? 0) + 1);
  });

  return displayPages
    .filter(({ spreadIndex }) => entriesPerSpread.get(spreadIndex) === 1)
    .map(({ spreadIndex }) => spreadIndex);
}

// Drag-to-reorder for a list of display pages: which entries may be dragged, and
// what to do when one is dropped. Both are derived from the list as presented, so
// the outcome matches what the user manipulated.
function useSpreadReorder(displayPages: DisplayPage[]) {
  const dispatch = useDispatch();
  const designData = useSelector(getDesignData);

  const eligibleSpreadIndexes = useMemo(() => getEligibleSpreadIndexes(displayPages), [displayPages]);

  const reorderableSpreadIndexes = useMemo(
    () => (designData ? getReorderableSpreadIndexes(designData, eligibleSpreadIndexes) : NO_SPREADS),
    [designData, eligibleSpreadIndexes],
  );

  const handleOrderChange = useCallback(
    (source: DraggableLocation, destination?: DraggableLocation | null) => {
      // dropped outside the list
      if (!destination) {
        return;
      }

      const fromSpreadIndex = displayPages[source.index]?.spreadIndex;
      const toSpreadIndex = displayPages[destination.index]?.spreadIndex;

      if (fromSpreadIndex === undefined || toSpreadIndex === undefined) {
        return;
      }

      dispatch(moveSpreadContentOperation(fromSpreadIndex, toSpreadIndex, eligibleSpreadIndexes));
    },
    [displayPages, eligibleSpreadIndexes, dispatch],
  );

  return { reorderableSpreadIndexes, handleOrderChange };
}

export { getEligibleSpreadIndexes };
export default useSpreadReorder;
